// Shapes and pictures formatted as PowerPoint formats them (SB, Features 4, 26 Oct 2026, David: "Slides that design like
// PowerPoint"): a fill that is a gradient (two to eight stops, straight at an angle or round from the middle) or a
// picture (stretched to cover, or tiled), an outline's dash and ends, a shadow, a glow and soft edges from small designed
// galleries, and for pictures a crop (what part of the picture shows), a crop to a shape, and brightness, contrast and
// saturation. Each is a field of its own on the box (shared/slideDesign.ts SlideBox), only where used: a box without them
// is drawn exactly as before, and nothing is migrated.
//
// Plain data, as everything on a slide: theme colours by name or #RRGGBB, numbers within limits, a picture only as a key
// the board keeps (so the week's clean-up, which reads every pic/ key in an entry's data, keeps a fill's picture too).

import { cleanPictureRef, type PictureRef } from "./imageInfo";
import { cleanColor, type ShapeKind, type SlideColor } from "./slideDesign";

// ---------------------------------------------------------------------------
// the fields

/** A gradient's colour at a point along it (0 at its start, 1 at its end), as see-through as its alpha says. */
export interface GradStop {
  at: number;
  color: SlideColor;
  /** 1 (left out) is solid */
  alpha?: number;
}

/** A gradient fill: straight across at an angle (CSS's: 0 runs up, 90 to the right, 180 down), or round from the middle. */
export interface BoxGradient {
  /** round from the middle out; straight when left out */
  radial?: true;
  angle: number;
  stops: GradStop[];
}

/** A picture fill: stretched to cover the shape (cropped to it, never squashed), or tiled at a scale of its own size. */
export interface FillPicture {
  image: PictureRef;
  tile?: true;
  /** a tile's size, times the picture's own pixels on the slide */
  scale?: number;
}

/** What part of a picture shows in its box: the share cut off each side (PowerPoint's crop, srcRect). */
export interface Crop {
  l: number;
  t: number;
  r: number;
  b: number;
}

/** A glow round a box: its size in the slide's pixels, a colour, and how solid. */
export interface Glow {
  size: number;
  color: SlideColor;
  alpha: number;
}

/** An outline's dashes, as PowerPoint names them: solid, dashes, round dots, square dots, dash dot, long dashes, long dash dot. */
export const DASHES = ["solid", "dash", "dot", "square", "dashDot", "long", "longDashDot"] as const;
export type DashStyle = (typeof DASHES)[number];
export const DASH_NAMES: Record<DashStyle, string> = { solid: "Solid", dash: "Dashes", dot: "Round dots", square: "Square dots", dashDot: "Dash dot", long: "Long dashes", longDashDot: "Long dash dot" };

/** A shadow, from a small designed gallery (as PowerPoint's Shape Effects offers its presets). */
export const SHADES = ["soft", "close", "lifted", "offset", "hard", "inner"] as const;
export type Shade = (typeof SHADES)[number];

export interface ShadeSpec {
  name: string;
  /** offset across and down, blur, in the slide's pixels; how dark (black at this alpha); inside the box */
  x: number;
  y: number;
  blur: number;
  alpha: number;
  inner?: true;
}

export const SHADE: Record<Shade, ShadeSpec> = {
  soft: { name: "Soft", x: 0, y: 10, blur: 28, alpha: 0.2 },
  close: { name: "Close", x: 0, y: 3, blur: 8, alpha: 0.28 },
  lifted: { name: "Lifted", x: 0, y: 26, blur: 56, alpha: 0.26 },
  offset: { name: "Offset", x: 9, y: 9, blur: 14, alpha: 0.34 },
  hard: { name: "Hard", x: 10, y: 10, blur: 0, alpha: 0.9 },
  inner: { name: "Inside", x: 0, y: 6, blur: 16, alpha: 0.32, inner: true },
};

/** The glows the gallery offers: three sizes in the accent, then the deck's second accent, its words' colour and its background's. */
export const GLOWS: { name: string; glow: Glow }[] = [
  { name: "Small accent glow", glow: { size: 10, color: "accent", alpha: 0.55 } },
  { name: "Accent glow", glow: { size: 20, color: "accent", alpha: 0.5 } },
  { name: "Wide accent glow", glow: { size: 36, color: "accent", alpha: 0.45 } },
  { name: "Second accent glow", glow: { size: 20, color: "accent2", alpha: 0.5 } },
  { name: "Ink glow", glow: { size: 20, color: "text", alpha: 0.35 } },
  { name: "Light glow", glow: { size: 20, color: "background", alpha: 0.7 } },
];

/** Soft edges the gallery offers, in the slide's pixels (PowerPoint's 2.5, 5, 10 and 25 point). */
export const SOFTS = [4, 8, 16, 42] as const;

/** Shapes a picture can be cropped to (Crop to shape), the circle first. */
export const MASKS: ShapeKind[] = ["ellipse", "round", "triangle", "rtriangle", "diamond", "pentagon", "hexagon", "arrow", "chevron", "star", "bubble", "plus"];

/** The fields SB adds to a box. */
export interface BoxFormat {
  /** a gradient fill, over the box's fill colour (shapes and text boxes) */
  grad?: BoxGradient;
  /** a picture fill, over a gradient and the fill colour (shapes and text boxes) */
  fillPic?: FillPicture;
  /** an outline's (and a line's) ends: flat when left out, as PowerPoint's */
  cap?: "round" | "square";
  /** a shadow from the gallery, over the plain soft shadow (`shadow`) */
  shade?: Shade;
  glow?: Glow;
  /** soft edges, in the slide's pixels */
  soft?: number;
  /** a picture's crop */
  crop?: Crop;
  /** a picture cropped to a shape */
  mask?: ShapeKind;
  /** a picture's brightness and contrast, from -1 to 1 (PowerPoint's -100% to +100%), and its saturation (0 grey, 1 as it is, up to 3) */
  bright?: number;
  contrast?: number;
  sat?: number;
}

/** The names of SB's fields, for code that carries a box's look from one box to another. */
export const FORMAT_KEYS = ["grad", "fillPic", "cap", "shade", "glow", "soft", "crop", "mask", "bright", "contrast", "sat"] as const;

// ---------------------------------------------------------------------------
// cleaners: each field as kept, or left out

const num = (v: unknown, lo: number, hi: number, digits = 3): number | undefined => {
  if (typeof v !== "number" || !Number.isFinite(v)) return undefined;
  const k = 10 ** digits;
  return Math.round(Math.max(lo, Math.min(hi, v)) * k) / k;
};

/** The most stops a gradient keeps. */
export const MAX_STOPS = 8;

export function cleanGradient(raw: unknown): BoxGradient | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const stops: GradStop[] = [];
  for (const s of Array.isArray(r.stops) ? r.stops.slice(0, MAX_STOPS) : []) {
    if (!s || typeof s !== "object") continue;
    const o = s as Record<string, unknown>;
    const color = cleanColor(o.color);
    const at = num(o.at, 0, 1);
    if (!color || at === undefined) continue;
    const alpha = num(o.alpha, 0, 1, 2);
    stops.push({ at, color, ...(alpha !== undefined && alpha < 1 ? { alpha } : {}) });
  }
  if (stops.length < 2) return undefined;
  stops.sort((a, b) => a.at - b.at);
  const a = num(r.angle, -3600, 3600, 1) ?? 180;
  return { ...(r.radial === true ? { radial: true as const } : {}), angle: ((a % 360) + 360) % 360, stops };
}

export function cleanFillPicture(raw: unknown): FillPicture | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const image = cleanPictureRef(r.image);
  if (!image) return undefined;
  const scale = num(r.scale, 0.05, 4, 3);
  return { image, ...(r.tile === true ? { tile: true as const } : {}), ...(r.tile === true && scale !== undefined && scale !== 1 ? { scale } : {}) };
}

/** A crop as kept: each side's share from 0 to 0.95, what shows at least 2% of the picture each way; none when nothing is cut. */
export function cleanCrop(raw: unknown): Crop | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  let l = num(r.l, 0, 0.95, 4) ?? 0;
  let t = num(r.t, 0, 0.95, 4) ?? 0;
  let rr = num(r.r, 0, 0.95, 4) ?? 0;
  let b = num(r.b, 0, 0.95, 4) ?? 0;
  if (l + rr > 0.98) {
    const k = 0.98 / (l + rr);
    l = Math.round(l * k * 1e4) / 1e4;
    rr = Math.round(rr * k * 1e4) / 1e4;
  }
  if (t + b > 0.98) {
    const k = 0.98 / (t + b);
    t = Math.round(t * k * 1e4) / 1e4;
    b = Math.round(b * k * 1e4) / 1e4;
  }
  return l || t || rr || b ? { l, t, r: rr, b } : undefined;
}

export function cleanGlow(raw: unknown): Glow | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const size = num(r.size, 1, 120, 1);
  const color = cleanColor(r.color);
  if (!size || !color) return undefined;
  // how solid it is, kept always (half when it says none, as PowerPoint's glow variations are)
  return { size, color, alpha: num(r.alpha, 0.05, 1, 2) ?? 0.5 };
}

const MASK_SET = new Set<string>(MASKS);

/**
 * SB's fields of a box, as kept, for the box's kind: fills on shapes and text boxes; outline ends on any with a border
 * or a line; effects on any but a line (a line keeps a shadow and a glow); crops and adjustments on pictures.
 */
export function cleanBoxFormat(r: Record<string, unknown>, kind: string): BoxFormat {
  const out: BoxFormat = {};
  const fillable = kind === "shape" || kind === "text";
  if (fillable) {
    const grad = cleanGradient(r.grad);
    if (grad) out.grad = grad;
    const fillPic = cleanFillPicture(r.fillPic);
    if (fillPic) out.fillPic = fillPic;
  }
  if (r.cap === "round" || r.cap === "square") out.cap = r.cap;
  if (kind !== "widget") {
    if (typeof r.shade === "string" && (SHADES as readonly string[]).includes(r.shade) && !(kind === "line" && r.shade === "inner")) out.shade = r.shade as Shade;
    const glow = cleanGlow(r.glow);
    if (glow) out.glow = glow;
  }
  if (kind !== "line" && kind !== "widget") {
    const soft = num(r.soft, 1, 120, 1);
    if (soft) out.soft = soft;
  }
  if (kind === "picture") {
    const crop = cleanCrop(r.crop);
    if (crop) out.crop = crop;
    if (typeof r.mask === "string" && MASK_SET.has(r.mask)) out.mask = r.mask as ShapeKind;
    const bright = num(r.bright, -1, 1, 2);
    const contrast = num(r.contrast, -1, 1, 2);
    const sat = num(r.sat, 0, 3, 2);
    if (bright) out.bright = bright;
    if (contrast) out.contrast = contrast;
    if (sat !== undefined && sat !== 1) out.sat = sat;
  }
  return out;
}

// ---------------------------------------------------------------------------
// gradients

/** The designed gradients the fill picker offers first, from the deck's theme colours (so they follow the theme). */
export const GRADIENTS: { name: string; grad: BoxGradient }[] = [
  { name: "Accent to second accent", grad: { angle: 135, stops: [{ at: 0, color: "accent" }, { at: 1, color: "accent2" }] } },
  { name: "Accent, deepening", grad: { angle: 180, stops: [{ at: 0, color: "accent" }, { at: 1, color: "text", alpha: 0.85 }] } },
  { name: "Accent, fading out", grad: { angle: 90, stops: [{ at: 0, color: "accent" }, { at: 1, color: "accent", alpha: 0 }] } },
  { name: "Panel", grad: { angle: 180, stops: [{ at: 0, color: "surface" }, { at: 1, color: "background" }] } },
  { name: "Night", grad: { angle: 160, stops: [{ at: 0, color: "text" }, { at: 1, color: "muted" }] } },
  { name: "Three accents", grad: { angle: 120, stops: [{ at: 0, color: "accent3" }, { at: 0.5, color: "accent" }, { at: 1, color: "accent4" }] } },
  { name: "Glow from the middle", grad: { radial: true, angle: 0, stops: [{ at: 0, color: "accent2" }, { at: 1, color: "accent" }] } },
  { name: "Spotlight", grad: { radial: true, angle: 0, stops: [{ at: 0, color: "surface" }, { at: 1, color: "text" }] } },
];

/** A colour at a point of a gradient, as #RRGGBB, with its alpha: for the readable ink on it and the stops bar. */
export function gradientAt(g: BoxGradient, at: number, hexOf: (c: SlideColor) => string): { hex: string; alpha: number } {
  const s = g.stops;
  if (at <= s[0].at) return { hex: hexOf(s[0].color), alpha: s[0].alpha ?? 1 };
  for (let i = 1; i < s.length; i++)
    if (at <= s[i].at) {
      const k = (at - s[i - 1].at) / (s[i].at - s[i - 1].at || 1);
      const a = rgbOf(hexOf(s[i - 1].color));
      const b = rgbOf(hexOf(s[i].color));
      return { hex: hexOfRgb(a.map((v, j) => v + (b[j] - v) * k) as [number, number, number]), alpha: (s[i - 1].alpha ?? 1) + ((s[i].alpha ?? 1) - (s[i - 1].alpha ?? 1)) * k };
    }
  const last = s[s.length - 1];
  return { hex: hexOf(last.color), alpha: last.alpha ?? 1 };
}

const rgbOf = (h: string): [number, number, number] => {
  const m = /^#?([0-9a-f]{6})/i.exec(h);
  const n = m ? parseInt(m[1], 16) : 0;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const hexOfRgb = (c: [number, number, number]) => "#" + c.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("").toUpperCase();

/** A gradient's stop added where it was clicked on the bar, in the colour the gradient has there. */
export function addStop(g: BoxGradient, at: number, hexOf: (c: SlideColor) => string): { grad: BoxGradient; index: number } {
  if (g.stops.length >= MAX_STOPS) return { grad: g, index: -1 };
  const a = Math.round(Math.max(0, Math.min(1, at)) * 1000) / 1000;
  const here = gradientAt(g, a, hexOf);
  const stops = [...g.stops, { at: a, color: here.hex as SlideColor, ...(here.alpha < 1 ? { alpha: Math.round(here.alpha * 100) / 100 } : {}) }].sort((x, y) => x.at - y.at);
  return { grad: { ...g, stops }, index: stops.findIndex((s) => s.at === a) };
}

/** A gradient with one stop taken out (a gradient keeps two). */
export function removeStop(g: BoxGradient, index: number): BoxGradient {
  if (g.stops.length <= 2) return g;
  return { ...g, stops: g.stops.filter((_, i) => i !== index) };
}

/** A gradient from one colour: that colour to a deeper version of it, as PowerPoint's gradient from a fill starts. */
export function gradientFrom(c: SlideColor | null | undefined): BoxGradient {
  const color = c ?? "accent";
  return { angle: 180, stops: [{ at: 0, color }, { at: 1, color: color === "text" ? "muted" : "text", alpha: 0.7 }] };
}

// ---------------------------------------------------------------------------
// pictures: what shows, as a crop

/**
 * The crop that shows what a picture shows now in a box of this size: filling it (cut to the box at its focus and zoom)
 * or, fitting inside it, all of it (none). For starting PowerPoint's Crop on a picture that has no crop of its own.
 */
export function cropOfView(img: { w: number; h: number }, box: { w: number; h: number; imgFit?: "fill" | "fit"; fx?: number; fy?: number; zoom?: number }): Crop | undefined {
  if (!img.w || !img.h || box.imgFit === "fit") return undefined;
  const s = Math.max(box.w / img.w, box.h / img.h) * (box.zoom ?? 1);
  const shownW = Math.min(1, box.w / (img.w * s));
  const shownH = Math.min(1, box.h / (img.h * s));
  const l = (1 - shownW) * (box.fx ?? 0.5);
  const t = (1 - shownH) * (box.fy ?? 0.5);
  return cleanCrop({ l, t, r: 1 - shownW - l, b: 1 - shownH - t });
}

/** Where the whole picture lies when cropped this way into a box at x, y, w, h: its own frame on the slide. */
export function pictureFrame(box: { x: number; y: number; w: number; h: number }, crop: Crop): { x: number; y: number; w: number; h: number } {
  const fw = box.w / Math.max(0.02, 1 - crop.l - crop.r);
  const fh = box.h / Math.max(0.02, 1 - crop.t - crop.b);
  return { x: box.x - crop.l * fw, y: box.y - crop.t * fh, w: fw, h: fh };
}

/** The crop that shows the part of a picture lying at `frame` (its whole frame on the slide) inside a box. */
export function cropIn(box: { x: number; y: number; w: number; h: number }, frame: { x: number; y: number; w: number; h: number }): Crop {
  const l = (box.x - frame.x) / frame.w;
  const t = (box.y - frame.y) / frame.h;
  const r = (frame.x + frame.w - box.x - box.w) / frame.w;
  const b = (frame.y + frame.h - box.y - box.h) / frame.h;
  return { l, t, r, b };
}

/**
 * A new picture in a cropped box (Replace picture): the box keeps its place and size, and the new picture fills it,
 * cut to the box around the middle the old crop showed, never squashed.
 */
export function cropForNew(img: { w: number; h: number }, box: { w: number; h: number }, old: Crop | undefined): Crop | undefined {
  if (!old || !img.w || !img.h) return undefined;
  const cx = (old.l + (1 - old.r)) / 2;
  const cy = (old.t + (1 - old.b)) / 2;
  const s = Math.max(box.w / img.w, box.h / img.h);
  const sw = Math.min(1, box.w / (img.w * s));
  const sh = Math.min(1, box.h / (img.h * s));
  const l = Math.max(0, Math.min(1 - sw, cx - sw / 2));
  const t = Math.max(0, Math.min(1 - sh, cy - sh / 2));
  return cleanCrop({ l, t, r: 1 - sw - l, b: 1 - sh - t });
}

/** A square crop in the middle of what a picture shows (for a circle), the box made square to match. */
export function squareCrop(img: { w: number; h: number }, box: { x: number; y: number; w: number; h: number }, crop: Crop | undefined): { crop: Crop | undefined; x: number; y: number; w: number; h: number } {
  const side = Math.min(box.w, box.h);
  const c = crop ?? cropOfView(img, box) ?? { l: 0, t: 0, r: 0, b: 0 };
  const frame = pictureFrame(box, c);
  const nb = { x: box.x + (box.w - side) / 2, y: box.y + (box.h - side) / 2, w: side, h: side };
  if (!img.w || !img.h) return { crop, ...nb };
  // the frame as drawn now stretches the picture to the box; keep its look by cutting the box down to a square inside it
  return { crop: cleanCrop(cropIn(nb, frame)), ...nb };
}

// ---------------------------------------------------------------------------
// the screen's look of a picture's adjustments: PowerPoint's brightness moves every colour toward white or black, its
// contrast spreads them from the middle grey or draws them in (DrawingML's luminance effect); saturation as CSS's

/** A slope and an intercept for each colour channel, for an SVG filter's linear transfer. */
export function adjustLinear(bright = 0, contrast = 0): { slope: number; intercept: number } {
  const c = Math.max(-1, Math.min(0.95, contrast));
  const slope = c >= 0 ? 1 / (1 - c) : 1 + c;
  return { slope: Math.round(slope * 1000) / 1000, intercept: Math.round((0.5 - 0.5 * slope + bright) * 1000) / 1000 };
}

// ---------------------------------------------------------------------------
// the slide's pixels in PowerPoint's units

/** EMU (914,400 an inch) in one of the slide's pixels (1,600 across 13.333 inches). */
export const EMU_PX = 7620;
